/**
 * FaqSection — eyebrow + heading + N question/answer pairs.
 * Path: faq.{eyebrow, heading, items[i].{q, a}}
 *
 * Add/remove/reorder.  The public page renders the LAST item as a full-width
 * card with a different style, so editors should keep their "what are you
 * waiting for?" CTA-style Q&A as the last entry.
 */

import {
  BilingualTextField, BilingualTextareaField,
  SectionPanel, ItemListEditor,
} from '../fields/BilingualFields'

const makeEmpty = () => ({ q: '', a: '' })

export default function FaqSection({ getField, setField, draftEn, draftAr, setSubtree }) {
  const itemsEn = (draftEn?.faq?.items) || []
  const itemsAr = (draftAr?.faq?.items) || []

  const onChange = (newEn, newAr) => {
    setSubtree('en', 'faq.items', newEn)
    setSubtree('ar', 'faq.items', newAr)
  }

  return (
    <SectionPanel title="FAQ" subtitle="Frequently asked questions — last item is rendered as a full-width CTA card on the public page">
      <BilingualTextField label="Eyebrow" path="faq.eyebrow" getField={getField} setField={setField} />
      <BilingualTextField label="Section heading" path="faq.heading" getField={getField} setField={setField} />

      <hr className="my-3 border-gray-200" />

      <ItemListEditor
        title="Questions"
        itemsEn={itemsEn}
        itemsAr={itemsAr}
        onChange={onChange}
        makeEmpty={() => ({ en: makeEmpty(), ar: makeEmpty() })}
        addLabel="Add question"
        minItems={1}
      >
        {({ count, moveUp, moveDown, remove }) => (
          <div className="space-y-3">
            {Array.from({ length: count }).map((_, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-gray-700">
                    Q{i + 1}{i === count - 1 && <span className="text-xs text-amber-600 ms-2">(rendered as full-width CTA card)</span>}
                  </h4>
                  <div className="flex items-center gap-1 text-xs">
                    <button type="button" onClick={() => moveUp(i)} className="px-2 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-40" disabled={i === 0}>↑</button>
                    <button type="button" onClick={() => moveDown(i)} className="px-2 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-40" disabled={i === count - 1}>↓</button>
                    <button type="button" onClick={() => remove(i)} className="px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40" disabled={count <= 1}>Remove</button>
                  </div>
                </div>
                <BilingualTextField label="Question" path={`faq.items.${i}.q`} getField={getField} setField={setField} />
                <BilingualTextareaField label="Answer" path={`faq.items.${i}.a`} getField={getField} setField={setField} rows={3} />
              </div>
            ))}
          </div>
        )}
      </ItemListEditor>
    </SectionPanel>
  )
}
